import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InvoiceItem } from "../../types/invoice";
import { formatEUR, formatDateES } from "../../utils/oc/ocCalc";

export interface InvoiceConfirmSummary {
  invoiceDate: string;
  clientLabel: string;
  issuerLabel: string;
  ocNumber: string;
  items: InvoiceItem[];
  total: number;
  notes?: string;
}

interface InvoiceConfirmDialogProps {
  open: boolean;
  summary: InvoiceConfirmSummary;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const SummaryRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", gap: 1, py: 0.4 }}>
    <Typography variant="pSmall" sx={{ minWidth: 130, opacity: 0.7 }}>
      {label}
    </Typography>
    <Typography variant="pSmall" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

// Resumen de confirmación antes de enviar la factura a Holded. Refleja
// exactamente los valores que se enviarán en el POST /api/invoices.
const InvoiceConfirmDialog: React.FC<InvoiceConfirmDialogProps> = ({
  open,
  summary,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      aria-labelledby="invoice-confirm-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="invoice-confirm-dialog-title" sx={{ pb: 1, px: 3 }}>
        <Typography variant="h5">{t("invoices.confirm.title")}</Typography>
        <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
          {t("invoices.confirm.subtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Box sx={{ mb: 2 }}>
          <SummaryRow
            label={t("invoices.confirm.date")}
            value={formatDateES(summary.invoiceDate)}
          />
          <SummaryRow
            label={t("invoices.confirm.client")}
            value={summary.clientLabel || "—"}
          />
          <SummaryRow
            label={t("invoices.confirm.issuer")}
            value={summary.issuerLabel || "—"}
          />
          <SummaryRow
            label={t("invoices.confirm.ocOrder")}
            value={summary.ocNumber || "—"}
          />
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  bgcolor: theme.palette.neutral["100"],
                },
              }}
            >
              <TableCell>{t("invoices.table.concepto")}</TableCell>
              <TableCell>{t("invoices.table.subtitulo")}</TableCell>
              <TableCell align="right">
                {t("invoices.table.subtotal")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.items.map((it, idx) => (
              <TableRow key={`confirm-item-${idx}`}>
                <TableCell>{it.concepto || "—"}</TableCell>
                <TableCell>{it.subtitulo || "—"}</TableCell>
                <TableCell align="right">
                  {formatEUR(Number(it.subtotal) || 0)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            mb: summary.notes ? 2 : 0,
          }}
        >
          <Typography variant="pMedium" sx={{ fontWeight: 700 }}>
            {t("invoices.totals.total")}
          </Typography>
          <Typography variant="pMedium" sx={{ fontWeight: 700 }}>
            {formatEUR(summary.total)}
          </Typography>
        </Box>

        {summary.notes && (
          <Box>
            <Typography
              variant="pSmall"
              sx={{ fontWeight: 600, opacity: 0.7, display: "block" }}
            >
              {t("invoices.notes")}
            </Typography>
            <Typography variant="pSmall" sx={{ whiteSpace: "pre-wrap" }}>
              {summary.notes}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          sx={{ color: (th) => th.palette.neutral[600], textTransform: "none" }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={18} color="inherit" /> : undefined
          }
          sx={{ textTransform: "none" }}
        >
          {t("invoices.confirm.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceConfirmDialog;
